import {
	DocStoreUnavailableError,
	type DocMeta,
	type DocStore,
	type StoredDocEnvelope,
	type SyncMeta,
	type SyncRevisionStore,
	type YDocPersistence,
} from './doc-store';

const DOCUMENTS = 'documents';
const THUMBNAILS = 'thumbnails';
const SYNC_META = 'syncMeta';
const YDOCS = 'ydocs';

type ThumbnailRecord = { id: string; blob: Blob };
type SyncMetaRecord = { id: string; revision: number; syncedUpdatedAt?: number };
type YDocRecord = { id: string; update: Uint8Array; savedAt: number };

function requestToPromise<T>(req: IDBRequest<T>): Promise<T> {
	return new Promise((resolve, reject) => {
		req.onsuccess = () => resolve(req.result);
		req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
	});
}

/** Request success only means the operation was queued into the
 *  transaction — IndexedDB can still abort the whole transaction at COMMIT
 *  time (e.g. quota exceeded). Callers that need a durability guarantee
 *  must await this after issuing their requests. */
function txDone(tx: IDBTransaction): Promise<void> {
	return new Promise((resolve, reject) => {
		tx.oncomplete = () => resolve();
		tx.onabort = () => reject(tx.error ?? new Error('IndexedDB transaction aborted'));
		tx.onerror = () => reject(tx.error ?? new Error('IndexedDB transaction failed'));
	});
}

export function isIndexedDbAvailable(): boolean {
	return typeof indexedDB !== 'undefined';
}

export type IndexedDbDocStoreConfig<TDoc extends StoredDocEnvelope, TMetaExtra> = {
	/** IndexedDB database name — one database per document type. */
	dbName: string;
	dbVersion: number;
	/** The load funnel: validates + migrates a raw record, THROWS on corrupt
	 *  documents — callers must not overwrite on throw. */
	parse: (raw: unknown) => TDoc;
	/** Builds one gallery-list entry from a raw (unparsed) record, or `null`
	 *  if the record is too corrupt to summarize — list() deliberately does
	 *  NOT run the full parse pipeline so one corrupt document can't blank
	 *  the gallery; it's just skipped. */
	metaFromRecord: (raw: unknown, thumbById: ReadonlyMap<string, Blob>) => DocMeta<TMetaExtra> | null;
	/** Adds the `ydocs` store (SP-52). Requires a dbVersion that created it. */
	yDocs?: boolean;
};

export class IndexedDbDocStore<TDoc extends StoredDocEnvelope, TMetaExtra>
implements DocStore<TDoc, TMetaExtra>, SyncRevisionStore, YDocPersistence {
	private readonly config: IndexedDbDocStoreConfig<TDoc, TMetaExtra>;
	private dbPromise: Promise<IDBDatabase> | null = null;

	constructor(config: IndexedDbDocStoreConfig<TDoc, TMetaExtra>) {
		this.config = config;
	}

	private openDb(): Promise<IDBDatabase> {
		if (this.dbPromise == null) {
			this.dbPromise = new Promise((resolve, reject) => {
				const req = indexedDB.open(this.config.dbName, this.config.dbVersion);
				req.onupgradeneeded = () => {
					const db = req.result;
					if (!db.objectStoreNames.contains(DOCUMENTS)) {
						db.createObjectStore(DOCUMENTS, { keyPath: 'id' });
					}
					if (!db.objectStoreNames.contains(THUMBNAILS)) {
						db.createObjectStore(THUMBNAILS, { keyPath: 'id' });
					}
					if (!db.objectStoreNames.contains(SYNC_META)) {
						db.createObjectStore(SYNC_META, { keyPath: 'id' });
					}
					if (this.config.yDocs && !db.objectStoreNames.contains(YDOCS)) {
						db.createObjectStore(YDOCS, { keyPath: 'id' });
					}
				};
				req.onsuccess = () => {
					// Lets FUTURE version bumps proceed: closing this connection on
					// versionchange means this tab won't be the one blocking the next
					// upgrade. A closed connection surfaces as a failed op on this tab,
					// which degrades to storage-unavailable and recovers on reload.
					req.result.onversionchange = () => {
						req.result.close();
						this.dbPromise = null;
					};
					resolve(req.result);
				};
				req.onerror = () => {
					this.dbPromise = null;
					const cause = req.error ?? new Error('IndexedDB open failed');
					reject(new DocStoreUnavailableError(cause.message, { cause }));
				};
				req.onblocked = () => {
					this.dbPromise = null;
					reject(new DocStoreUnavailableError('IndexedDB upgrade blocked by another open tab — close other StitchPress tabs and reload'));
				};
			});
		}
		return this.dbPromise;
	}

	async list(): Promise<DocMeta<TMetaExtra>[]> {
		const db = await this.openDb();
		const tx = db.transaction([DOCUMENTS, THUMBNAILS], 'readonly');
		const docs = await requestToPromise(tx.objectStore(DOCUMENTS).getAll() as IDBRequest<unknown[]>);
		const thumbs = await requestToPromise(tx.objectStore(THUMBNAILS).getAll() as IDBRequest<ThumbnailRecord[]>);
		const thumbById = new Map(thumbs.map((t) => [t.id, t.blob]));
		return docs
			.map((d) => this.config.metaFromRecord(d, thumbById))
			.filter((m): m is DocMeta<TMetaExtra> => m != null)
			.sort((a, b) => b.updatedAt - a.updatedAt);
	}

	async load(id: string): Promise<TDoc | null> {
		const db = await this.openDb();
		const tx = db.transaction(DOCUMENTS, 'readonly');
		const raw = await requestToPromise(tx.objectStore(DOCUMENTS).get(id));
		if (raw == null) return null;
		return this.config.parse(raw);
	}

	async save(doc: TDoc): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(DOCUMENTS, 'readwrite');
		await requestToPromise(tx.objectStore(DOCUMENTS).put(doc));
		await txDone(tx);
	}

	async saveThumbnail(id: string, blob: Blob): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(THUMBNAILS, 'readwrite');
		await requestToPromise(tx.objectStore(THUMBNAILS).put({ id, blob } satisfies ThumbnailRecord));
		await txDone(tx);
	}

	/** All cached thumbnails, keyed by id — including ones with no matching
	 *  DOCUMENTS record (a remote-only diagram whose blob `backfillThumbnails`
	 *  cached but which this device has never opened). `list()` deliberately
	 *  cannot surface those, since it builds each meta from a document record;
	 *  the sync layer reads them here instead. See SyncRevisionStore. */
	async getThumbnails(): Promise<Map<string, Blob>> {
		const db = await this.openDb();
		const tx = db.transaction(THUMBNAILS, 'readonly');
		const thumbs = await requestToPromise(tx.objectStore(THUMBNAILS).getAll() as IDBRequest<ThumbnailRecord[]>);
		return new Map(thumbs.map((t) => [t.id, t.blob]));
	}

	async delete(id: string): Promise<void> {
		const db = await this.openDb();
		const stores = this.ownedStores();
		const tx = db.transaction(stores, 'readwrite');
		for (const name of stores) await requestToPromise(tx.objectStore(name).delete(id));
		await txDone(tx);
	}

	async getRevision(id: string): Promise<number | null> {
		const db = await this.openDb();
		const tx = db.transaction(SYNC_META, 'readonly');
		const raw = await requestToPromise(tx.objectStore(SYNC_META).get(id) as IDBRequest<SyncMetaRecord | undefined>);
		return raw?.revision ?? null;
	}

	async getSyncMeta(id: string): Promise<SyncMeta | null> {
		const db = await this.openDb();
		const tx = db.transaction(SYNC_META, 'readonly');
		const raw = await requestToPromise(tx.objectStore(SYNC_META).get(id) as IDBRequest<SyncMetaRecord | undefined>);
		if (raw == null) return null;
		return { revision: raw.revision, syncedUpdatedAt: raw.syncedUpdatedAt ?? null };
	}

	async setRevision(id: string, revision: number, syncedUpdatedAt: number): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(SYNC_META, 'readwrite');
		await requestToPromise(tx.objectStore(SYNC_META).put({ id, revision, syncedUpdatedAt } satisfies SyncMetaRecord));
		await txDone(tx);
	}

	async deleteRevision(id: string): Promise<void> {
		const db = await this.openDb();
		const tx = db.transaction(SYNC_META, 'readwrite');
		await requestToPromise(tx.objectStore(SYNC_META).delete(id));
		await txDone(tx);
	}

	/** Store names delete()/clearAll() touch — includes ydocs when enabled. */
	private ownedStores(): string[] {
		return this.config.yDocs ? [DOCUMENTS, THUMBNAILS, SYNC_META, YDOCS] : [DOCUMENTS, THUMBNAILS, SYNC_META];
	}

	get hasYDocs(): boolean {
		return this.config.yDocs === true;
	}

	private requireYDocs(): void {
		if (!this.config.yDocs) throw new Error(`${this.config.dbName} has no Yjs store`);
	}

	async loadYDoc(id: string): Promise<Uint8Array | null> {
		this.requireYDocs();
		const db = await this.openDb();
		const tx = db.transaction(YDOCS, 'readonly');
		const raw = await requestToPromise(tx.objectStore(YDOCS).get(id) as IDBRequest<YDocRecord | undefined>);
		return raw?.update ?? null;
	}

	async saveYDoc(id: string, update: Uint8Array): Promise<void> {
		this.requireYDocs();
		const db = await this.openDb();
		const tx = db.transaction(YDOCS, 'readwrite');
		await requestToPromise(tx.objectStore(YDOCS).put({ id, update, savedAt: Date.now() } satisfies YDocRecord));
		await txDone(tx);
	}

	async deleteYDoc(id: string): Promise<void> {
		this.requireYDocs();
		const db = await this.openDb();
		const tx = db.transaction(YDOCS, 'readwrite');
		await requestToPromise(tx.objectStore(YDOCS).delete(id));
		await txDone(tx);
	}

	/** Wipes every document, thumbnail, sync-revision and Yjs record — used
	 *  when a different user signs in on this browser (see cache-ownership.ts):
	 *  the previous account's cache must not leak into the new one. */
	async clearAll(): Promise<void> {
		const db = await this.openDb();
		const stores = this.ownedStores();
		const tx = db.transaction(stores, 'readwrite');
		for (const name of stores) await requestToPromise(tx.objectStore(name).clear());
		await txDone(tx);
	}
}
